import { ParsedUrlQuery } from 'querystring'
import {
  AccountTree,
  AssignmentOutlined,
  AutorenewOutlined,
  CorporateFare,
  ErrorOutline,
  FolderOpen,
  HourglassEmpty,
  InsertChartOutlined,
  MonetizationOnOutlined,
  NotificationsOutlined,
  RequestQuoteOutlined,
  FindInPageOutlined,
  ShoppingCartOutlined,
} from '@mui/icons-material'
import { Roles } from '@omnea-digital/toolbox'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useIsHostedCataloguesEnabled } from '@omnea-app/fe-hosted-catalogues/useIsHostedCataloguesEnabled'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useIsPunchoutCataloguesEnabled } from '@omnea-app/fe-punchout-catalogues/useIsPunchoutCataloguesEnabled'
import { generateRequestsListRoute } from '@omnea-app/fe-requests/requestRoutes'
import { useSavingsDashboardLdFlags } from '@omnea-app/fe-savings-dashboard/useSavingsDashboardLdFlags'
import { useGetMyTaskCount } from '@omnea-app/fe-tasks/useTasks'
import { StatsContext } from '@omnea-app/frontend/providers/StatsProvider'
import { useAppShellLdFlags } from '../launch-darkly'
import { SidebarPages } from './SidebarPages'
import { SidebarUncontrolled } from './SidebarUncontrolled'

interface Props {
  path: string | null
}

export const resolvePage = (path: string | null, query: ParsedUrlQuery) => {
  switch (path) {
    case '/suppliers/[[...mode]]':
      return { page: SidebarPages.SUPPLIERS, isExpanded: true }
    case '/suppliers/detail/[id]':
    case '/suppliers/detail/[id]/[tab]':
      return { page: SidebarPages.SUPPLIERS, isExpanded: false }
    case '/subprocessors/[[...mode]]':
      return { page: SidebarPages.SUBPROCESSORS, isExpanded: true }
    case '/assessments/[[...mode]]':
      return { page: SidebarPages.ASSESSMENTS, isExpanded: true }
    case '/agreements/[[...mode]]':
    case '/agreements/detail/[id]':
    case '/agreements/detail/[id]/[tab]':
      return { page: SidebarPages.AGREEMENTS, isExpanded: true }
    case '/renewals/[[...mode]]':
      return { page: SidebarPages.RENEWALS, isExpanded: true }
    case '/tasks/[[...mode]]':
      return { page: SidebarPages.TASKS, isExpanded: true }
    case '/approvals/[requestStepId]':
    case '/approvals/[requestStepId]/tasks/[requestStepTaskId]':
      return { page: SidebarPages.TASKS, isExpanded: false }
    case '/requests/[[...mode]]':
      return { page: SidebarPages.REQUESTS, isExpanded: true }
    case '/requests/detail/[id]/[tab]':
    case '/requests/detail/[id]/my-tasks/[taskIdOrFormId]':
    case '/requests/detail/[id]/submissions/[requestFormId]':
    case '/requests/detail/[id]/rfx/[section]':
    case '/requests/detail/[id]/renewal-assessment/[requestFormId]':
    case '/requests/detail/[id]/rfx':
    case '/requests/detail/[id]/rfx/[sideNav]':
    case '/requests/detail/[id]/rfx/[sideNav]/[requestFormTemplateId]':
    case '/requests/detail/[id]/rfx/[sideNav]/[requestFormTemplateId]/supplier-forms':
    case '/requests/detail/[id]/rfx/[sideNav]/deadlines':
    case '/requests/detail/[id]/rfx/[sideNav]/ai-scoring':
    case '/requests/detail/[id]/rfx/[sideNav]/results-breakdown':
    case '/requests/detail/[id]/rfx/[sideNav]/results-breakdown/[requestFormTemplateId]':
    case '/requests/detail/[id]/rfx/[sideNav]/[requestFormTemplateId]/pricing-analysis':
    case '/requests/detail/[id]/rfx/[sideNav]/scoring/[requestStepTaskId]':
    case '/requests/detail/[id]/auctions/[sideNav]':
    case '/requests/detail/[id]/steps/[requestStepId]':
    case '/requests/detail/[id]/steps/[requestStepId]/tasks/[requestStepTaskId]':
    case '/requests/detail/[id]/audit-log':
      return { page: SidebarPages.REQUESTS, isExpanded: false }
    case '/renewal-assessment/[requestStepId]':
    case '/renewal-assessment/[requestStepId]/tasks/[requestStepTaskId]':
    case '/supplier-onboarding/[requestId]/[requestStepId]/overview':
    case '/supplier-onboarding/[requestId]/[requestStepId]/overview/tasks/[taskId]':
      return { page: SidebarPages.REQUESTS, isExpanded: false }
    case '/workflows':
    case '/workflows/[[...mode]]':
      return { page: SidebarPages.WORKFLOW_LIST, isExpanded: true }
    case '/workflows/edit/[id]':
      return { page: SidebarPages.WORKFLOW_LIST, isExpanded: false }
    case '/start/[[...tab]]': {
      if (query.tab?.includes('drafts')) return { page: SidebarPages.DRAFTS, isExpanded: false }
      return { page: SidebarPages.CREATE_REQUEST, isExpanded: false }
    }
    case '/intake/[...id]':
    case '/intake/completion/[id]':
      return { page: SidebarPages.CREATE_REQUEST, isExpanded: false }
    case '/settings':
      return { page: SidebarPages.HOME, isExpanded: true }
    case '/settings/[[...mode]]': {
      const mode = query.mode
      if (!mode) return { page: SidebarPages.HOME, isExpanded: true }
      if (mode[0] === 'organisation' && mode[1] === 'users-and-groups') {
        return { page: SidebarPages.OGR_USERS_AND_GROUPS, isExpanded: true }
      } else if (mode[0] === 'organisation' && mode[1] === 'user-properties') {
        return { page: SidebarPages.USER_PROPERTIES, isExpanded: true }
      } else if (mode[0] === 'organisation' && mode[1] === 'security') {
        return { page: SidebarPages.SECURITY, isExpanded: true }
      } else if (mode[0] === 'organisation' && mode[1] === 'core-data') {
        return { page: SidebarPages.CORE_DATA, isExpanded: true }
      } else if (mode[0] === 'api') {
        return { page: SidebarPages.API, isExpanded: true }
      } else if (mode[0] === 'integrations') {
        return { page: SidebarPages.INTEGRATIONS, isExpanded: true }
      } else if (mode[0] === 'workflows') {
        return { page: SidebarPages.WORKFLOW_LIST, isExpanded: true }
      }
      return { page: SidebarPages.HOME, isExpanded: true }
    }
    case '/forms':
      return { page: SidebarPages.FORMS, isExpanded: true }
    case '/slack-file-upload':
    case '/supplier-onboarding/[requestId]/[requestStepId]/overview':
    case '/supplier-onboarding/[requestId]/[requestStepId]/overview/tasks/[taskId]':
      return { page: SidebarPages.HOME, isExpanded: false }
    case '/settings/dynamic-assignees/[[...mode]]':
      return { page: SidebarPages.BUDGET_APPROVALS, isExpanded: true }
    case '/reporting':
    case '/reporting/data-destinations':
    case '/reporting/[[...path]]':
    case '/insights':
    case '/insights/[[...path]]':
      return { page: SidebarPages.REPORTING, isExpanded: true }
    case '/documents':
    case '/documents/[[...mode]]':
      return { page: SidebarPages.DOCUMENTS, isExpanded: true }
    case '/risks':
    case '/risks/[[...mode]]':
      return { page: SidebarPages.RISKS, isExpanded: true }
    case '/risks/detail/[id]':
      return { page: SidebarPages.RISKS, isExpanded: false }
    case '/risks/settings':
      return { page: SidebarPages.RISK_SETTINGS, isExpanded: true }
    case '/risk-alerts':
    case '/risk-alerts/[[...mode]]':
      return { page: SidebarPages.RISK_ALERTS, isExpanded: true }
    case '/budgets/[[...mode]]':
      return { page: SidebarPages.BUDGETS, isExpanded: true }
    case '/sourcing/[[...mode]]':
      return { page: SidebarPages.SAVINGS_DASHBOARD, isExpanded: true }
    case '/':
      return { page: SidebarPages.HOME, isExpanded: true }

    // Hide sidebar for these pages
    case '/login/[[...id]]':
    case '/redeem/[challenge]':
    case '/choose-organisation':
    case '/settings/workflows/edit/[id]':
      return { page: SidebarPages.NONE, isExpanded: false }
    case '/products-and-services':
      return { page: SidebarPages.PRODUCT_CATALOGUES, isExpanded: false }
    case '/product-catalogue':
      return { page: SidebarPages.PRODUCT_CATALOGUE, isExpanded: false }
    case '/product-brands/[[...mode]]':
      return { page: SidebarPages.PRODUCT_BRANDS, isExpanded: true }
    case '/ai':
    case '/ai/[mode]':
      return { page: SidebarPages.AI, isExpanded: true }
    default:
      return { page: SidebarPages.HOME, isExpanded: true }
  }
}

export function Sidebar({ path }: Props) {
  const { t } = useTranslation('common')
  const { query } = useRouter()
  const { isAdmin, isNonViewerAdmin, role } = useAuthenticatedUser().user
  const { page: selectedPage, isExpanded: isExpandedExternal } = resolvePage(path, query)

  const {
    ENABLE_ASSESSMENTS,
    BUDGETS,
    TPRM,
    TPRM_CONTINUOUS_MONITORING,
    ENABLE_ASSIGNEE_PERMISSIONS,
    CREATE_AGREEMENT: ENABLE_AGREEMENTS,
    SUPPLIER_SUBPROCESSORS,
  } = useFeatureFlags()
  const isPunchoutCataloguesEnabled = useIsPunchoutCataloguesEnabled()
  const isHostedCataloguesEnabled = useIsHostedCataloguesEnabled()
  const showProductCatalogues = isPunchoutCataloguesEnabled || isHostedCataloguesEnabled
  const { RELEASE_S_AND_P_SAVINGS_DASHBOARD } = useSavingsDashboardLdFlags()
  const { RELEASE_INSIGHTS_CUSTOM_DASHBOARDS } = useAppShellLdFlags()
  const { numAssessmentsActionNeeded, numRenewalsActionNeeded } = useContext(StatsContext)

  const { data: taskCount = 0 } = useGetMyTaskCount()

  const suppliersItem = {
    type: SidebarPages.SUPPLIERS,
    title: t('sidebar.resources-suppliers'),
    Icon: CorporateFare,
    href: '/suppliers',
    selected: selectedPage === SidebarPages.SUPPLIERS,
  }
  const subprocessorsItem = {
    type: SidebarPages.SUBPROCESSORS,
    title: t('sidebar.resources-subprocessors'),
    Icon: AccountTree,
    href: '/subprocessors',
    selected: selectedPage === SidebarPages.SUBPROCESSORS,
  }
  const assessmentsItem = {
    type: SidebarPages.ASSESSMENTS,
    title: t('sidebar.resources-assessments'),
    Icon: AssignmentOutlined,
    href: '/assessments',
    selected: selectedPage === SidebarPages.ASSESSMENTS,
    count: numAssessmentsActionNeeded ?? undefined,
  }
  const renewalsItem = {
    type: SidebarPages.RENEWALS,
    title: t('sidebar.resources-renewals'),
    Icon: AutorenewOutlined,
    href: '/renewals',
    selected: selectedPage === SidebarPages.RENEWALS,
    count: numRenewalsActionNeeded ?? undefined,
  }

  const risksItem = {
    type: SidebarPages.RISKS,
    title: t('sidebar.resources-risks'),
    Icon: ErrorOutline,
    href: '/risks',
    selected: selectedPage === SidebarPages.RISKS,
  }

  const riskManagementChildren = [
    ...(TPRM ? [risksItem] : []),
    ...(TPRM_CONTINUOUS_MONITORING
      ? [
          {
            type: SidebarPages.RISK_ALERTS,
            title: t('sidebar.resources-alerts'),
            Icon: NotificationsOutlined,
            href: '/risk-alerts',
            selected: selectedPage === SidebarPages.RISK_ALERTS,
          },
        ]
      : []),
    ...(SUPPLIER_SUBPROCESSORS ? [subprocessorsItem] : []),
  ]

  const riskManagementItem = {
    type: SidebarPages.RISK_MANAGEMENT,
    title: t('sidebar.resources-risk-management'),
    Icon: ErrorOutline,
    href: riskManagementChildren[0]?.href ?? '/risks',
    selected: [SidebarPages.RISK_MANAGEMENT, SidebarPages.RISKS, SidebarPages.RISK_ALERTS, SidebarPages.SUBPROCESSORS].includes(
      selectedPage
    ),
    children: riskManagementChildren,
  }

  const documentsItem = {
    type: SidebarPages.DOCUMENTS,
    title: t('sidebar.resources-documents'),
    Icon: FolderOpen,
    href: '/documents',
    selected: selectedPage === SidebarPages.DOCUMENTS,
  }

  const agreementsItem = {
    type: SidebarPages.AGREEMENTS,
    title: t('sidebar.resources-agreements'),
    Icon: RequestQuoteOutlined,
    href: '/agreements',
    selected: selectedPage === SidebarPages.AGREEMENTS,
  }

  const reportingItem = {
    type: SidebarPages.REPORTING,
    title: RELEASE_INSIGHTS_CUSTOM_DASHBOARDS ? t('sidebar.resources-insights') : t('sidebar.resources-reporting'),
    Icon: InsertChartOutlined,
    // Insights home is /insights/custom-dashboards; link straight there to avoid a client redirect.
    href: RELEASE_INSIGHTS_CUSTOM_DASHBOARDS ? '/insights/custom-dashboards' : '/reporting',
    selected: selectedPage === SidebarPages.REPORTING,
  }

  const budgetsItem = {
    type: SidebarPages.BUDGETS,
    title: t('sidebar.resources-budgets'),
    Icon: MonetizationOnOutlined,
    href: '/budgets',
    selected: selectedPage === SidebarPages.BUDGETS,
  }

  const productCataloguesItem = {
    type: SidebarPages.PRODUCT_CATALOGUES,
    title: t('sidebar.resources-product-catalogues'),
    Icon: ShoppingCartOutlined,
    href: '/products-and-services',
    selected: selectedPage === SidebarPages.PRODUCT_CATALOGUES,
  }

  const savingsDashboardItem = {
    type: SidebarPages.SAVINGS_DASHBOARD,
    title: t('sidebar.resources-savings-dashboard'),
    Icon: FindInPageOutlined,
    href: '/sourcing',
    selected: selectedPage === SidebarPages.SAVINGS_DASHBOARD,
  }

  const adminList = [
    suppliersItem,
    ...(ENABLE_ASSESSMENTS ? [assessmentsItem] : []),
    ...(ENABLE_AGREEMENTS ? [agreementsItem] : []),
    renewalsItem,
    reportingItem,
    ...(riskManagementChildren.length > 1 ? [riskManagementItem] : riskManagementChildren),
    documentsItem,
    ...(BUDGETS ? [budgetsItem] : []),
    ...(showProductCatalogues ? [productCataloguesItem] : []),
    ...(RELEASE_S_AND_P_SAVINGS_DASHBOARD ? [savingsDashboardItem] : []),
  ]

  const viewerList = [
    suppliersItem,
    ...(ENABLE_ASSESSMENTS ? [assessmentsItem] : []),
    ...(ENABLE_AGREEMENTS ? [agreementsItem] : []),
    renewalsItem,
    ...(riskManagementChildren.length > 1 ? [riskManagementItem] : riskManagementChildren),
    documentsItem,
    ...(BUDGETS ? [budgetsItem] : []),
    ...(showProductCatalogues ? [productCataloguesItem] : []),
    ...(RELEASE_S_AND_P_SAVINGS_DASHBOARD ? [savingsDashboardItem] : []),
  ]

  const userList = [
    ...(ENABLE_ASSIGNEE_PERMISSIONS ? [suppliersItem] : []),
    ...(ENABLE_ASSIGNEE_PERMISSIONS && ENABLE_ASSESSMENTS ? [assessmentsItem] : []),
    ...(ENABLE_AGREEMENTS ? [agreementsItem] : []),
    ...(ENABLE_ASSIGNEE_PERMISSIONS ? [renewalsItem] : []),
    ...(riskManagementChildren.length > 1 ? [riskManagementItem] : riskManagementChildren),
    ...(ENABLE_ASSIGNEE_PERMISSIONS ? [documentsItem] : []),
    ...(showProductCatalogues ? [productCataloguesItem] : []),
    ...(RELEASE_S_AND_P_SAVINGS_DASHBOARD ? [savingsDashboardItem] : []),
  ]

  const requestSidebarPageRouteItem = {
    type: SidebarPages.REQUESTS,
    title: t('sidebar.resources-requests'),
    Icon: HourglassEmpty,
    href: generateRequestsListRoute(),
    selected: selectedPage === SidebarPages.REQUESTS,
  }

  const items = [requestSidebarPageRouteItem, ...(isAdmin ? (isNonViewerAdmin ? adminList : viewerList) : userList)]
  const [isExpanded, toggleExpanded] = useState(isExpandedExternal)

  useEffect(() => {
    toggleExpanded(isExpandedExternal)
  }, [isExpandedExternal])

  if (role === Roles.SUPPLIER) {
    return null
  }

  if (selectedPage === SidebarPages.NONE) {
    return null
  }

  return (
    <SidebarUncontrolled
      isSidebarExpanded={isExpanded}
      onToggleExpanded={toggleExpanded}
      selectedPage={selectedPage}
      items={items}
      numberOfActiveTasks={taskCount}
      path={path}
    />
  )
}
